<template>
  <div class="flex flex-col space-y-8">
    <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
      <TieredChallengeStoreCard
        class="md:col-span-3"
        :store-name="currentStoreName"
        :total-sales="totalSales"
        :seller-count="sellerCount"
        :leads-count="totalLeads"
        :counting-method="challenge.countingMethod"
        :store-options="storeOptions"
        :selected-store-id="selectedStoreId"
        :has-multiple-stores="hasMultipleStores"
        @store-change="$emit('store-change', $event)"
      />
      <TieredChallengeTeamReward :total-reward="totalReward" />
      <TieredChallengeStoreRank
        v-if="hasStoreRankings"
        :current-rank="storeRankings.currentStoreRank"
        :total-stores="storeRankings.totalStores"
      />
    </div>

    <div>
      <p class="text-xl font-bold mb-4">{{ t("individual_tracking") }}</p>
      <div class="grid grid-cols-1 gap-4">
        <TieredChallengeSellerCard
          v-for="seller in displayedSellers"
          :key="seller.id"
          :seller="seller"
          :sales-targets="challenge.salesTargets || []"
          :counting-method="challenge.countingMethod"
        />
      </div>
      <button
        v-if="hasMoreSellers"
        class="mt-4 w-full py-2 text-sm text-primary-dark font-medium bg-white rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
        @click="sellersExpanded = !sellersExpanded"
      >
        {{ sellersExpanded ? t("show_less") : t("show_more") }}
      </button>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <TieredChallengeSellerRanking
        v-if="hasSellers"
        :sellers="sellers"
        :sales-targets="challenge.salesTargets || []"
        :counting-method="challenge.countingMethod"
      />
      <TieredChallengeNetworkRanking
        v-if="hasStoreRankings"
        :store-rankings="storeRankings"
        :current-store-id="selectedStoreId"
        :counting-method="challenge.countingMethod"
      />
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import TieredChallengeStoreCard from "@/components/challenge/tiered/TieredChallengeStoreCard.vue";
import TieredChallengeTeamReward from "@/components/challenge/tiered/TieredChallengeTeamReward.vue";
import TieredChallengeStoreRank from "@/components/challenge/tiered/TieredChallengeStoreRank.vue";
import TieredChallengeSellerCard from "@/components/challenge/tiered/TieredChallengeSellerCard.vue";
import TieredChallengeSellerRanking from "@/components/challenge/tiered/TieredChallengeSellerRanking.vue";
import TieredChallengeNetworkRanking from "@/components/challenge/tiered/TieredChallengeNetworkRanking.vue";

export default {
  components: {
    TieredChallengeStoreCard,
    TieredChallengeTeamReward,
    TieredChallengeStoreRank,
    TieredChallengeSellerCard,
    TieredChallengeSellerRanking,
    TieredChallengeNetworkRanking,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: {
    challenge: {
      type: Object,
      required: true,
    },
    performance: {
      type: Object,
      required: true,
    },
    storeOptions: {
      type: Array,
      default: () => [],
    },
    selectedStoreId: {
      default: null,
    },
    hasMultipleStores: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["store-change"],

  data() {
    return {
      sellersExpanded: false,
    };
  },

  computed: {
    sellers() {
      return this.performance.sellers || [];
    },

    storeRankings() {
      return this.performance.storeRankings || {};
    },

    currentStoreName() {
      if (this.hasStoreRankings && this.selectedStoreId) {
        const store = this.storeRankings.stores.find((s) => s.storeId === this.selectedStoreId);
        return store?.salesChannel || "";
      }
      return "";
    },

    totalSales() {
      return this.sellers.reduce((sum, seller) => sum + (seller.score || 0), 0);
    },

    totalLeads() {
      const raw = this.performance.storeLeadsCount || 0;
      return Math.min(raw, this.totalSales);
    },

    totalReward() {
      return this.sellers.reduce((sum, seller) => sum + (seller.rewardAmount || 0), 0);
    },

    sellerCount() {
      return this.sellers.length;
    },

    hasSellers() {
      return this.sellers.length > 0;
    },

    hasStoreRankings() {
      return this.storeRankings.stores && this.storeRankings.stores.length > 0;
    },

    displayedSellers() {
      return this.sellersExpanded ? this.sellers : this.sellers.slice(0, 4);
    },

    hasMoreSellers() {
      return this.sellers.length > 4;
    },
  },
};
</script>

<i18n src="./TieredChallenge.i18n.json"></i18n>
